import { useState, useEffect, useRef } from "react";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { useFetchUserLeagues } from "../../hooks/league/useFetchUserLeagues";
import { useAuth0 } from "@auth0/auth0-react";
import SubmissionDisplay from "../submissions/SubmissionDisplay";

// Add custom styles for smooth scrolling
const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .snap-x {
    scroll-snap-type: x mandatory;
  }
  .snap-center {
    scroll-snap-align: center;
  }
  .smooth-scroll {
    scroll-behavior: smooth;
  }
`;

const ActiveSubmissionsCard = ({ leagueId = null, embedded = false }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { data: dbUser, isLoading: isUserLoading } = useFetchUser();
  const { data: leaguesData, isLoading: isLeaguesLoading } =
    useFetchUserLeagues();
  const [expandedSubmissions, setExpandedSubmissions] = useState({});
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  // Fetch all active submissions when user and leagues are loaded
  useEffect(() => {
    const fetchAllSubmissions = async () => {
      if (!dbUser?._id) return;

      let leaguesToFetch = [];

      if (leagueId) {
        // If a specific leagueId is provided (e.g., global competition),
        // fetch submissions for that league directly
        leaguesToFetch = [{ _id: leagueId, leagueName: "Global Competition" }];
      } else {
        // Otherwise, fetch from all active leagues the user is a member of
        if (!leaguesData?.memberOf) return;
        leaguesToFetch = leaguesData.memberOf.filter(
          (league) => league.status === "active",
        );
      }

      if (leaguesToFetch.length === 0) {
        setAllSubmissions([]);
        return;
      }

      setIsLoadingSubmissions(true);

      try {
        const token = await getAccessTokenSilently();
        const apiUrl = process.env.REACT_APP_API_URL;

        const submissionsPromises = leaguesToFetch.map(async (league) => {
          try {
            const response = await fetch(
              `${apiUrl}/api/lineup/active/${league._id}/${dbUser._id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );

            if (!response.ok) {
              console.error(
                `Failed to fetch submissions for league ${league._id}`,
              );
              return [];
            }

            const submissions = await response.json();
            return submissions.map((submission) => ({
              ...submission,
              leagueId: league._id,
              leagueName: league.leagueName || "Unknown League",
            }));
          } catch (error) {
            console.error(
              `Error fetching submissions for league ${league._id}:`,
              error,
            );
            return [];
          }
        });

        const results = await Promise.all(submissionsPromises);
        const flattenedSubmissions = results.flat();
        setAllSubmissions(flattenedSubmissions);
      } catch (error) {
        console.error("Error fetching submissions:", error);
        setAllSubmissions([]);
      } finally {
        setIsLoadingSubmissions(false);
      }
    };

    fetchAllSubmissions();
  }, [dbUser?._id, leaguesData, getAccessTokenSilently, leagueId]);

  // Track scroll position to update active indicator
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || allSubmissions.length <= 1) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth =
        container.querySelector(".submission-card")?.offsetWidth || 0;
      const gap = 16; // 4 * 4px (gap-4)
      const index = Math.round(scrollLeft / (cardWidth + gap));
      setActiveIndex(Math.min(index, allSubmissions.length - 1));
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [allSubmissions.length]);

  const toggleSubmission = (submissionId) => {
    setExpandedSubmissions((prev) => ({
      ...prev,
      [submissionId]: !prev[submissionId],
    }));
  };

  if (isUserLoading || isLeaguesLoading || isLoadingSubmissions) {
    return (
      <div className={embedded ? "" : "bg-white rounded-xl shadow-card p-6"}>
        <h2 className="text-xl font-bold text-dark-grey mb-4">
          Active Submissions
        </h2>
        <div className="flex justify-center py-8">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 bg-gray-200 rounded-full mb-3"></div>
            <div className="h-4 bg-gray-200 rounded w-32"></div>
          </div>
        </div>
      </div>
    );
  }

  if (allSubmissions.length === 0) {
    return (
      <div className={embedded ? "" : "bg-white rounded-xl shadow-card p-6"}>
        <h2 className="text-xl font-bold text-dark-grey mb-4">
          Active Submissions
        </h2>
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-dark-grey mb-2">
            No Active Submissions
          </h3>
          <p className="text-medium-grey">
            You haven't submitted any lineups yet. Create your first lineup to
            get started!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={embedded ? "" : "bg-white rounded-xl shadow-card p-4 md:p-6"}
    >
      <style>{scrollbarHideStyles}</style>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg md:text-xl font-bold text-dark-grey">
          Active Submissions
        </h2>
        <span className="text-xs md:text-sm text-medium-grey bg-light-grey px-2 md:px-3 py-1 rounded-full">
          {allSubmissions.length} submission
          {allSubmissions.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Horizontal Scrollable Container */}
      <div className={`relative ${embedded ? "" : "-mx-4 md:-mx-6"}`}>
        <div
          ref={scrollContainerRef}
          className={`overflow-x-auto scrollbar-hide smooth-scroll snap-x ${embedded ? "" : "px-4 md:px-6"}`}
        >
          <div className="flex gap-4 pb-2">
            {allSubmissions.map((submission) => {
              const isExpanded = expandedSubmissions[submission._id];

              return (
                <SubmissionDisplay
                  key={submission._id}
                  submission={submission}
                  showLeagueName={true}
                  showEventName={true}
                  showViewEventButton={true}
                  isExpanded={isExpanded}
                  onToggle={() => toggleSubmission(submission._id)}
                  className="submission-card flex-shrink-0 snap-center w-[85vw] sm:w-[400px] md:w-[450px]"
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      {allSubmissions.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {allSubmissions.map((submission, idx) => (
            <div
              key={submission._id}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === activeIndex ? "bg-deep-red" : "bg-gray-300"
              }`}
              title={`${submission.leagueName} - ${submission.eventName}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveSubmissionsCard;
